'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';

function ArticleCard({ post, isAdminView, onDelete }) {
    return (
        <div className="group relative">
            {/* Main clickable card */}
            <Link href={`/blog/${post.slug}`} className="block">
                <div className="relative overflow-hidden rounded-xl bg-muted">
                    <div className="relative aspect-[16/9] w-full">
                        {post.cover ? (
                            <Image
                                src={post.cover}
                                alt={post.title}
                                fill
                                className="object-cover transition duration-300 group-hover:scale-[1.02]"
                                sizes="(max-width: 768px) 100vw, 50vw"
                            />
                        ) : (
                            <div className="absolute inset-0 bg-gradient-to-br from-muted via-background to-muted" />
                        )}
                    </div>
                </div>

                <div className="mt-3 space-y-2">
                    <h3 className="fs-24 font-semibold leading-snug text-foreground group-hover:underline">
                        {post.title}
                    </h3>
                </div>
            </Link>

            {/* Admin actions */}
            {isAdminView ? (
                <div className="absolute right-3 top-3 flex gap-2">
                    <Link
                        href={`/admin/blog/${post.slug}`}
                        onClick={(e) => e.stopPropagation()}
                        className="rounded bg-white/90 px-3 py-1 text-base font-semibold shadow-sm backdrop-blur hover:bg-white"
                    >
                        Edit
                    </Link>

                    <button
                        type="button"
                        onClick={(e) => {
                            e.preventDefault(); // prevent Link navigation
                            e.stopPropagation();
                            onDelete(post.slug);
                        }}
                        className="rounded bg-red-600/90 px-3 py-1 text-base font-semibold text-white shadow-sm hover:bg-red-600"
                    >
                        Delete
                    </button>
                    <span
                        className={`rounded px-3 py-1 text-base font-semibold shadow-sm backdrop-blur capitalize ${post.status === 'published' ? 'bg-green-600 text-white' : 'bg-black text-white'}`}
                    >
                        {post.status}
                    </span>
                </div>
            ) : null}
        </div>
    );
}

export default function BlogListing({ initialData = [], pagination = {}, isAdminView = false }) {
    const [posts, setPosts] = useState(initialData);
    const [page, setPage] = useState(Number(pagination.page ?? pagination.currentPage ?? 1));
    const [totalPages, setTotalPages] = useState(Number(pagination.totalPages ?? 1));
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        setPosts(initialData);
        setPage(Number(pagination.page ?? pagination.currentPage ?? 1));
        setTotalPages(Number(pagination.totalPages ?? 1));
    }, [initialData, pagination]);

    const fetchPosts = async (newPage) => {
        setIsLoading(true);
        try {
            const endpoint = isAdminView
                ? `/api/admin/blog?page=${newPage}&limit=10`
                : `/api/blog?page=${newPage}&limit=10`;

            const res = await fetch(endpoint);
            if (!res.ok) throw new Error('Failed to fetch');

            const data = await res.json();
            setPosts(data.data ?? []);
            setTotalPages(Number(data.pagination?.totalPages ?? 1));
            setPage(newPage);
        } finally {
            setIsLoading(false);
        }
    };

    const handlePaginationChange = (newPage) => {
        if (newPage > 0 && newPage <= totalPages && newPage !== page) {
            fetchPosts(newPage);
        }
    };

    const handleDelete = async (slug) => {
        if (!confirm('Delete this blog post?')) return;

        try {
            const res = await fetch(`/api/admin/blog/${encodeURIComponent(slug)}`, {
                method: 'DELETE',
            });
            const data = await res.json();

            if (!res.ok) {
                alert(data?.error || 'Failed to delete');
                return;
            }

            // remove from UI
            setPosts((prev) => prev.filter((p) => p.slug !== slug));
        } catch {
            alert('Network error');
        }
    };

    return (
        <div>
            <div>
                {!posts.length && !isLoading ? (
                    <div className="py-20 text-center text-neutral-500">No blog posts found.</div>
                ) : (
                    <>
                        <div className="grid gap-18 sm:grid-cols-2">
                            {posts.map((p) => (
                                <ArticleCard
                                    key={p.slug}
                                    post={p}
                                    isAdminView={isAdminView}
                                    onDelete={handleDelete}
                                />
                            ))}
                        </div>
                    </>
                )}
            </div>

            <div className="mt-8 flex justify-between items-center">
                <button
                    onClick={() => handlePaginationChange(page - 1)}
                    disabled={page === 1 || isLoading}
                    className="px-4 py-2 bg-gray-200 text-gray-600 rounded-md hover:bg-gray-300 disabled:opacity-60"
                >
                    Prev
                </button>

                <span>
                    Page {page} of {totalPages}
                </span>

                <button
                    onClick={() => handlePaginationChange(page + 1)}
                    disabled={page === totalPages || isLoading}
                    className="px-4 py-2 bg-gray-200 text-gray-600 rounded-md hover:bg-gray-300 disabled:opacity-60"
                >
                    Next
                </button>
            </div>
        </div>
    );
}
